import React, { useEffect, useState } from "react";
import axios from "axios";
import { AuthInitialData } from "../types/interface";
import { deleteCookies, getCookie, setCookie } from "../utils/common";
import { userAPI } from "../services/api/user";

const initialData: AuthInitialData = {
  token: "",
  refreshToken: "",
  firstName: "",
  lastName: "",
  email: "",
  profilePic: "",
  userUniqueID: "",
  isLoggedIn: false,
  tenants: [],
  currentTenant: "",
  userRole: "",
  login: () => {},
  logout: () => {},
  setTenant: () => "",
};

export const AuthContext = React.createContext(initialData);

const AuthContextProvider = (props: any) => {
  const cookieToken = getCookie("token") ? getCookie("token") : "";
  const cookieRefreshToken = getCookie("refresh_token")
    ? getCookie("refresh_token")
    : "";
  const cookieFirstName = getCookie("first_name")
    ? getCookie("first_name")
    : "";
  const cookieLastName = getCookie("last_name") ? getCookie("last_name") : "";
  const cookieProfilePic = getCookie("profile_pic")
    ? getCookie("profile_pic")
    : "";
  const cookieEmail = getCookie("email") ? getCookie("email") : "";
  const cookieUserUniqueID = getCookie("unique_user_id")
    ? getCookie("unique_user_id")
    : "";
  const cookieCurrentTenant = getCookie("current_tenant")
    ? getCookie("current_tenant")
    : "";
  const [token, setToken] = useState(cookieToken);
  const [refreshToken, setRefreshToken] = useState(cookieRefreshToken);
  const [firstName, setFirstName] = useState(cookieFirstName);
  const [lastName, setLastName] = useState(cookieLastName);
  const [profilePic, setProfilePic] = useState(cookieProfilePic);
  const [email, setEmail] = useState(cookieEmail);
  const [tenants, setTenants] = useState(initialData.tenants);
  const [userUniqueID, setUserUniqueID] = useState(cookieUserUniqueID);
  const [currentTenant, setCurrentTenant] = useState(cookieCurrentTenant);
  const [userRole, setUserRole] = useState("");

  const isUserLoggedIn = !!token;

  // Call tenants if tenants list are not known
  useEffect(() => {
    if (isUserLoggedIn) {
      if (tenants && !tenants.length) {
        getTenants();
      }
    }
  }, [isUserLoggedIn]);

  const getTenants = () => {
    userAPI
      .getSingle?.(userUniqueID)
      .then((response) => {
        setTenants(response.tenants);
        setUserRole(response.role.name.toLowerCase());
        // If user is part of only one tenant
        if (
          !currentTenant &&
          response.tenants &&
          response.tenants.length === 1
        ) {
          setCurrentTenant(response.tenants[0].slug);
          setCookie("current_tenant", response.tenants[0].slug);
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const logoutHandler = () => {
    setToken("");
    setRefreshToken("");
    setFirstName("");
    setLastName("");
    setEmail("");
    setProfilePic("");
    setUserUniqueID("");
    setCurrentTenant("");
    deleteCookies();
  };

  const loginHandler = (value: { [key: string]: any }) => {
    const {
      token,
      refresh_token,
      user: {
        first_name,
        last_name,
        email,
        profile_pic,
        tenants,
        unique_user_id,
      },
    } = value;
    if (token) {
      axios.defaults.headers.common["Authorization"] =
        token && `Bearer ${token}`;
    }
    setToken(token);
    setRefreshToken(refresh_token);
    setFirstName(first_name);
    setLastName(last_name);
    setEmail(email);
    setProfilePic(profile_pic);
    setUserUniqueID(unique_user_id);
    setCookie("token", token);
    setCookie("refresh_token", refresh_token);
    setCookie("first_name", first_name);
    setCookie("last_name", last_name);
    setCookie("email", email);
    setCookie("profile_pic", profile_pic);
    setCookie("unique_user_id", unique_user_id);
  };

  const setTenantHandler = (slug: string, uuid: string) => {
    setCurrentTenant(slug);
    setCookie("current_tenant", slug);
    setCookie("current_tenant_uuid", uuid);
  };

  const contextValue = {
    token: token,
    refreshToken: refreshToken,
    isLoggedIn: isUserLoggedIn,
    firstName: firstName,
    lastName: lastName,
    profilePic: profilePic,
    email: email,
    userUniqueID: userUniqueID,
    tenants: tenants,
    currentTenant: currentTenant,
    userRole: userRole,
    login: loginHandler,
    logout: logoutHandler,
    setTenant: setTenantHandler,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {props.children}
    </AuthContext.Provider>
  );
};

export default AuthContextProvider;
